import { Link } from 'react-router-dom';
import { useState } from 'react';
import Sidebar from '../components/Sidebar';

function CreateProject() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Project Details
    projectName: '',
    description: '',
    type: 'kubernetes',
    owner: '',

    // Step 2: Configuration
    cloudProvider: 'aws',
    region: 'us-east-1',
    environment: 'production',
    autoScaling: true,
    monitoring: true,

    // Step 3: Review
    confirmed: false,
  });

  const steps = [
    { number: 1, title: 'Project Details', description: 'Basic information about your project' },
    { number: 2, title: 'Configuration', description: 'Infrastructure and deployment settings' },
    { number: 3, title: 'Review', description: 'Confirm your project settings' },
  ];

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    // Handle form submission
    console.log('Submitting project:', formData);
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg-primary)]">
      <Sidebar />

      <div className="ml-64 p-8">
        {/* Header */}
        <header className="mb-8 fade-in-up">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-display mb-2" style={{ color: 'var(--color-text-primary)' }}>
                Create New Project
              </h1>
              <p style={{ color: 'var(--color-text-secondary)' }}>
                Set up your infrastructure project in three simple steps
              </p>
            </div>
            <Link to="/projects" className="btn-secondary">
              Cancel
            </Link>
          </div>
        </header>

        {/* Progress Indicator */}
        <div className="card mb-8 fade-in-up delay-100">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.number} className="flex items-center flex-1">
                <div className="flex items-center space-x-4">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-bold transition-all ${
                      currentStep >= step.number
                        ? 'text-white'
                        : 'border-2'
                    }`}
                    style={
                      currentStep >= step.number
                        ? { backgroundColor: 'var(--color-primary)' }
                        : { borderColor: 'var(--color-border)', color: 'var(--color-text-muted)' }
                    }
                  >
                    {currentStep > step.number ? (
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      step.number
                    )}
                  </div>
                  <div className="hidden md:block">
                    <h3
                      className="font-semibold"
                      style={{ color: currentStep >= step.number ? 'var(--color-primary)' : 'var(--color-text-muted)' }}
                    >
                      {step.title}
                    </h3>
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{step.description}</p>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className="flex-1 h-0.5 mx-4"
                    style={{ backgroundColor: currentStep > step.number ? 'var(--color-primary)' : 'var(--color-border)' }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="card fade-in-up delay-200">
          {/* Step 1: Project Details */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--color-text-primary)' }}>
                  Project Details
                </h2>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                  Project Name *
                </label>
                <input
                  type="text"
                  placeholder="my-awesome-project"
                  value={formData.projectName}
                  onChange={(e) => handleInputChange('projectName', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg transition-colors"
                  style={{
                    backgroundColor: 'var(--color-bg-light)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)'
                  }}
                />
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                  Use lowercase letters, numbers, and hyphens only
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                  Description *
                </label>
                <textarea
                  placeholder="Describe what this project is for..."
                  rows={4}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg resize-none transition-colors"
                  style={{
                    backgroundColor: 'var(--color-bg-light)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)'
                  }}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                    Project Type *
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg transition-colors"
                    style={{
                      backgroundColor: 'var(--color-bg-light)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-primary)'
                    }}
                  >
                    <option value="kubernetes">Kubernetes Cluster</option>
                    <option value="aws">AWS Infrastructure</option>
                    <option value="gcp">GCP Infrastructure</option>
                    <option value="azure">Azure Infrastructure</option>
                    <option value="data">Data Pipeline</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                    Project Owner *
                  </label>
                  <input
                    type="text"
                    placeholder="owner@company.com"
                    value={formData.owner}
                    onChange={(e) => handleInputChange('owner', e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg transition-colors"
                    style={{
                      backgroundColor: 'var(--color-bg-light)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-primary)'
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Configuration */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--color-text-primary)' }}>
                  Infrastructure Configuration
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                    Cloud Provider *
                  </label>
                  <select
                    value={formData.cloudProvider}
                    onChange={(e) => handleInputChange('cloudProvider', e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg transition-colors"
                    style={{
                      backgroundColor: 'var(--color-bg-light)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-primary)'
                    }}
                  >
                    <option value="aws">Amazon Web Services (AWS)</option>
                    <option value="gcp">Google Cloud Platform (GCP)</option>
                    <option value="azure">Microsoft Azure</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                    Region *
                  </label>
                  <select
                    value={formData.region}
                    onChange={(e) => handleInputChange('region', e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg transition-colors"
                    style={{
                      backgroundColor: 'var(--color-bg-light)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-primary)'
                    }}
                  >
                    <option value="us-east-1">US East (N. Virginia)</option>
                    <option value="us-west-2">US West (Oregon)</option>
                    <option value="eu-west-1">EU (Ireland)</option>
                    <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                  Environment *
                </label>
                <select
                  value={formData.environment}
                  onChange={(e) => handleInputChange('environment', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg transition-colors"
                  style={{
                    backgroundColor: 'var(--color-bg-light)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)'
                  }}
                >
                  <option value="development">Development</option>
                  <option value="staging">Staging</option>
                  <option value="production">Production</option>
                </select>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  Additional Features
                </h3>

                <label className="flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-colors hover:bg-[var(--color-bg-elevated)]" style={{ borderColor: 'var(--color-border)' }}>
                  <input
                    type="checkbox"
                    checked={formData.autoScaling}
                    onChange={(e) => handleInputChange('autoScaling', e.target.checked)}
                    className="w-5 h-5 rounded"
                    style={{ accentColor: 'var(--color-primary)' }}
                  />
                  <div className="flex-1">
                    <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Auto-scaling</p>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      Automatically scale resources based on demand
                    </p>
                  </div>
                </label>

                <label className="flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-colors hover:bg-[var(--color-bg-elevated)]" style={{ borderColor: 'var(--color-border)' }}>
                  <input
                    type="checkbox"
                    checked={formData.monitoring}
                    onChange={(e) => handleInputChange('monitoring', e.target.checked)}
                    className="w-5 h-5 rounded"
                    style={{ accentColor: 'var(--color-primary)' }}
                  />
                  <div className="flex-1">
                    <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Monitoring & Alerts</p>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      Enable comprehensive monitoring and alerting
                    </p>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--color-text-primary)' }}>
                  Review Your Project
                </h2>
                <p style={{ color: 'var(--color-text-secondary)' }}>
                  Please review your project configuration before creating it
                </p>
              </div>

              <div className="space-y-6">
                <div className="p-6 bg-[var(--color-bg-elevated)] rounded-lg border" style={{ borderColor: 'var(--color-border)' }}>
                  <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
                    Project Details
                  </h3>
                  <dl className="space-y-3">
                    <div className="flex justify-between">
                      <dt className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Project Name:</dt>
                      <dd className="text-sm font-mono" style={{ color: 'var(--color-text-primary)' }}>{formData.projectName || 'Not set'}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Description:</dt>
                      <dd className="text-sm text-right" style={{ color: 'var(--color-text-primary)' }}>{formData.description || 'Not set'}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Type:</dt>
                      <dd className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{formData.type}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Owner:</dt>
                      <dd className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{formData.owner || 'Not set'}</dd>
                    </div>
                  </dl>
                </div>

                <div className="p-6 bg-[var(--color-bg-elevated)] rounded-lg border" style={{ borderColor: 'var(--color-border)' }}>
                  <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
                    Infrastructure Configuration
                  </h3>
                  <dl className="space-y-3">
                    <div className="flex justify-between">
                      <dt className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Cloud Provider:</dt>
                      <dd className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{formData.cloudProvider.toUpperCase()}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Region:</dt>
                      <dd className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{formData.region}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Environment:</dt>
                      <dd className="text-sm capitalize" style={{ color: 'var(--color-text-primary)' }}>{formData.environment}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Auto-scaling:</dt>
                      <dd className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{formData.autoScaling ? 'Enabled' : 'Disabled'}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Monitoring:</dt>
                      <dd className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{formData.monitoring ? 'Enabled' : 'Disabled'}</dd>
                    </div>
                  </dl>
                </div>
              </div>

              <div className="p-4 bg-blue-500/20 border border-blue-500/50 rounded-lg">
                <p className="text-sm text-blue-300">
                  <strong>Note:</strong> Once created, the project will begin provisioning immediately. This may take 5-10 minutes.
                </p>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="mt-8 pt-6 border-t flex items-center justify-between" style={{ borderColor: 'var(--color-border)' }}>
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </span>
            </button>

            <div className="flex items-center space-x-2">
              {steps.map((step) => (
                <div
                  key={step.number}
                  className="w-2 h-2 rounded-full transition-all"
                  style={{ backgroundColor: currentStep >= step.number ? 'var(--color-primary)' : 'var(--color-border)' }}
                />
              ))}
            </div>

            {currentStep < 3 ? (
              <button onClick={handleNext} className="btn-primary">
                <span className="flex items-center">
                  Next
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </button>
            ) : (
              <button onClick={handleSubmit} className="btn-primary">
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Create Project
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateProject;
